import React, { useState, useEffect } from 'react';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';

import {
    FaTrophy,
    FaCoins,
    FaUserFriends,
    FaChartLine,
    FaQuestionCircle,
    FaChevronLeft,
    FaChevronRight,
    FaSpinner,
    FaCrown,
    FaAward,
    FaStar,
    FaGem,
} from 'react-icons/fa';
import {
    MdLeaderboard,
    MdAnalytics,
    MdPerson,
    MdTimer
} from 'react-icons/md';
import {
    IoPeople
} from 'react-icons/io5';
import { FiCalendar } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import getISTDate from '../../components/getISTDate';

// MainButton component for date filters
const MainButton = ({ onClick, label, selectState }) => {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${selectState
                ? 'bg-indigo-500 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
        >
            {label}
        </button>
    );
};

const LeaderBoard = () => {
    const [leaderboardData, setLeaderboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [startDate, setStartDate] = useState(getISTDate());
    const [endDate, setEndDate] = useState(getISTDate());
    const [filterType, setFilterType] = useState('today'); // 'today', 'yesterday', 'thisWeek', 'thisMonth', 'range'

    const navigate = useNavigate();

    // Handle form date changes
    const handleFormDate = (e) => {
        const { name, value } = e.target;
        if (name === 'startDate') {
            setStartDate(value);
        } else if (name === 'endDate') {
            setEndDate(value);
        }
        setFilterType('range');
    };

    // Fetch leaderboard data
    const fetchLeaderboardData = async () => {
        setLoading(true);
        try {
            const response = await Calling.getAdminMainLeaderboard(page, limit, filterType, startDate, endDate);
            if (response.success) {
                setLeaderboardData(response.data);
            } else {
                toast.error(response.message || 'Failed to fetch leaderboard data');
            }
        } catch (error) {
            console.error('Error fetching leaderboard:', error);
            toast.error('Error fetching leaderboard data');
        } finally {
            setLoading(false);
        }
    };

    // Initial load and refresh on filter changes
    useEffect(() => {
        fetchLeaderboardData();
    }, [page, limit, filterType]);

    // Effect to refresh data when date range changes if filter type is 'range'
    useEffect(() => {
        if (filterType === 'range') {
            fetchLeaderboardData();
        }
    }, [startDate, endDate]);

    // Handle page change
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= (leaderboardData?.pagination?.totalPages || 1)) {
            setPage(newPage);
        }
    };

    const handleEdit = (id) => {
        navigate(`/manage/user/all/info/${id}`);
    };

    // Format date for display
    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);
            const now = new Date();
            const diffInSeconds = Math.floor((now - date) / 1000);

            if (diffInSeconds < 60) {
                return 'just now';
            }

            const diffInMinutes = Math.floor(diffInSeconds / 60);
            if (diffInMinutes < 60) {
                return `${diffInMinutes}m ago`;
            }

            const diffInHours = Math.floor(diffInMinutes / 60);
            if (diffInHours < 24) {
                return `${diffInHours}h ago`;
            }

            const diffInDays = Math.floor(diffInHours / 24);
            if (diffInDays < 30) {
                return `${diffInDays}d ago`;
            }

            const diffInMonths = Math.floor(diffInDays / 30);
            if (diffInMonths < 12) {
                return `${diffInMonths}mo ago`;
            }

            const diffInYears = Math.floor(diffInDays / 365);
            return `${diffInYears}y ago`;
        } catch (error) {
            return 'Unknown';
        }
    };

    const handleDateRangeFilter = (filter) => {
        const todayUTC = new Date();

        // Convert UTC date to IST (UTC +5:30) in YYYY-MM-DD format
        const toIST = (date) => {
            const istOffset = 5.5 * 60 * 60 * 1000; // 5 hours 30 min in ms
            return new Date(date.getTime() + istOffset).toISOString().split('T')[0];
        };

        let start, end;

        switch (filter) {
            case 'today':
                start = toIST(todayUTC);
                end = start;
                break;

            case 'yesterday':
                const yesterdayUTC = new Date(todayUTC);
                yesterdayUTC.setDate(todayUTC.getDate() - 1);
                start = toIST(yesterdayUTC);
                end = start;
                break;

            case 'thisWeek':
                const firstDayOfWeekUTC = new Date(todayUTC);
                firstDayOfWeekUTC.setDate(todayUTC.getDate() - todayUTC.getDay());
                const lastDayOfWeekUTC = new Date(firstDayOfWeekUTC);
                lastDayOfWeekUTC.setDate(firstDayOfWeekUTC.getDate() + 6);

                start = toIST(firstDayOfWeekUTC);
                end = toIST(lastDayOfWeekUTC);
                break;

            case 'thisMonth':
                const firstDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth(), 1);
                const lastDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);

                start = toIST(firstDayOfMonthUTC);
                end = toIST(lastDayOfMonthUTC);
                break;

            default:
                start = '';
                end = '';
        }

        setStartDate(start);
        setEndDate(end);
        setFilterType(filter);
    };


    // Get trophy icon and styling based on rank
    const getRankDisplay = (rank) => {
        switch (rank) {
            case 1:
                return {
                    icon: <FaCrown className="w-6 h-6 text-yellow-500" />,
                    bgColor: 'bg-gradient-to-r from-yellow-100 to-yellow-200',
                    borderColor: 'border-yellow-300',
                    textColor: 'text-yellow-800'
                };
            case 2:
                return {
                    icon: <FaGem className="w-6 h-6 text-gray-500" />,
                    bgColor: 'bg-gradient-to-r from-gray-100 to-gray-200',
                    borderColor: 'border-gray-300',
                    textColor: 'text-gray-800'
                };
            case 3:
                return {
                    icon: <FaAward className="w-6 h-6 text-orange-500" />,
                    bgColor: 'bg-gradient-to-r from-orange-100 to-orange-200',
                    borderColor: 'border-orange-300',
                    textColor: 'text-orange-800'
                };
            default:
                return {
                    icon: <FaStar className="w-5 h-5 text-blue-500" />,
                    bgColor: 'bg-gradient-to-r from-blue-50 to-blue-100',
                    borderColor: 'border-blue-200',
                    textColor: 'text-blue-800'
                };
        }
    };

    // Render pagination controls
    const renderPagination = () => {
        if (!leaderboardData || leaderboardData.pagination.totalPages <= 1) return null;

        const { currentPage, totalPages } = leaderboardData.pagination;

        return (
            <div className="flex justify-center items-center gap-3 mt-8">
                <button
                    className="px-4 py-2 bg-white border-2 border-gray-200 rounded-xl font-semibold text-gray-600 hover:bg-indigo-500 hover:text-white hover:border-indigo-500 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-600 disabled:hover:border-gray-200"
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                >
                    First
                </button>
                <button
                    className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 rounded-xl font-semibold text-gray-600 hover:bg-indigo-500 hover:text-white hover:border-indigo-500 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-600 disabled:hover:border-gray-200"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                >
                    <FaChevronLeft className="w-4 h-4" />
                    Previous
                </button>

                <div className="px-6 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-bold border-2 border-indigo-500">
                    Page {currentPage} of {totalPages}
                </div>

                <button
                    className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 rounded-xl font-semibold text-gray-600 hover:bg-indigo-500 hover:text-white hover:border-indigo-500 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-600 disabled:hover:border-gray-200"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                >
                    Next
                    <FaChevronRight className="w-4 h-4" />
                </button>
                <button
                    className="px-4 py-2 bg-white border-2 border-gray-200 rounded-xl font-semibold text-gray-600 hover:bg-indigo-500 hover:text-white hover:border-indigo-500 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-600 disabled:hover:border-gray-200"
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                >
                    Last
                </button>
            </div>
        );
    };

    if (loading && !leaderboardData) {
        return (
            <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
                <div className="text-center bg-white rounded-3xl shadow-2xl p-12 border border-white/20">
                    <FaSpinner className="animate-spin w-16 h-16 text-indigo-600 mx-auto mb-6" />
                    <p className="text-xl font-semibold text-slate-700">Loading leaderboard data...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
            <div className="max-w-7xl mx-auto mt-6">
                {/* Header */}
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl p-6 mb-8 border border-indigo-100">
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl">
                                <MdLeaderboard className="w-8 h-8 text-white" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-slate-800">Global Leaderboard</h1>
                                <p className="text-slate-600">Track top performers and quiz champions</p>
                            </div>
                        </div>

                        {/* Filter Controls */}
                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-4">
                                <div className="flex items-center gap-3 mb-2">
                                    <MdAnalytics className="w-4 h-4 text-indigo-600" />
                                    <span className="font-semibold text-slate-700">Results Per Page</span>
                                </div>
                                <select
                                    value={limit}
                                    onChange={(e) => {
                                        setLimit(Number(e.target.value));
                                        setPage(1);
                                    }}
                                    className="w-full p-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 bg-gray-50 focus:bg-white"
                                >
                                    <option value={10}>10 Users</option>
                                    <option value={20}>20 Users</option>
                                    <option value={50}>50 Users</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Date Range Filter */}
                <div className="bg-white rounded-xl shadow-sm border border-main-border overflow-hidden mb-8">
                    <div className="p-6">
                        <div className="flex items-center mb-4">
                            <FiCalendar className="text-indigo-600 mr-2" />
                            <h2 className="text-lg font-semibold text-slate-800">Date Range</h2>
                        </div>
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-12 md:col-span-8 flex flex-wrap gap-3">
                                <MainButton
                                    onClick={() => handleDateRangeFilter('today')}
                                    label={'Today'}
                                    selectState={filterType === "today"}
                                />
                                <MainButton
                                    onClick={() => handleDateRangeFilter('yesterday')}
                                    label={'Yesterday'}
                                    selectState={filterType === "yesterday"}
                                />
                                <MainButton
                                    onClick={() => handleDateRangeFilter('thisWeek')}
                                    label={'This Week'}
                                    selectState={filterType === "thisWeek"}
                                />
                                <MainButton
                                    onClick={() => handleDateRangeFilter('thisMonth')}
                                    label={'This Month'}
                                    selectState={filterType === "thisMonth"}
                                />
                            </div>

                            {/* Date Range Form */}
                            <div className="col-span-12 md:col-span-4 flex justify-end">
                                <form onSubmit={(e) => { e.preventDefault(); fetchLeaderboardData(); }} className="w-full md:w-auto">
                                    <div className="grid grid-cols-12 gap-3 justify-items-end">
                                        <div className="col-span-6 relative">
                                            <input
                                                type="date"
                                                name="startDate"
                                                value={startDate}
                                                onChange={handleFormDate}
                                                min="2023-01-01"
                                                max={new Date().toISOString().split('T')[0]}
                                                className={`w-full px-4 py-2 rounded-lg shadow-sm border cursor-pointer bg-white ${filterType === "range"
                                                    ? 'border-indigo-500 ring-2 ring-indigo-200 text-indigo-600'
                                                    : 'border-gray-200 text-slate-700 hover:border-indigo-500'
                                                    }`}
                                            />
                                        </div>
                                        <div className="col-span-6 relative">
                                            <input
                                                type="date"
                                                name="endDate"
                                                value={endDate}
                                                onChange={handleFormDate}
                                                min="2023-01-01"
                                                max={new Date().toISOString().split('T')[0]}
                                                className={`w-full px-4 py-2 rounded-lg shadow-sm border cursor-pointer bg-white ${filterType === "range"
                                                    ? 'border-indigo-500 ring-2 ring-indigo-200 text-indigo-600'
                                                    : 'border-gray-200 text-slate-700 hover:border-indigo-500'
                                                    }`}
                                            />
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats Cards */}
                {leaderboardData && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-xl">
                                    <FaCoins className="w-8 h-8 text-white" />
                                </div>
                                <div>
                                    <p className="text-slate-600 font-semibold">Total Coins Distributed</p>
                                    <p className="text-3xl font-bold text-slate-800">
                                        {leaderboardData.stats.totalCoinsDistributed.toLocaleString()}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl">
                                    <FaQuestionCircle className="w-8 h-8 text-white" />
                                </div>
                                <div>
                                    <p className="text-slate-600 font-semibold">Total Quizzes Completed</p>
                                    <p className="text-3xl font-bold text-slate-800">
                                        {leaderboardData.stats.totalQuizzes.toLocaleString()}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-all duration-300">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl">
                                    <FaChartLine className="w-8 h-8 text-white" />
                                </div>
                                <div>
                                    <p className="text-slate-600 font-semibold">Average Coins Per Quiz</p>
                                    <p className="text-3xl font-bold text-slate-800">
                                        {leaderboardData.stats.averageCoinsPerQuiz}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Leaderboard Table */}
                {leaderboardData && leaderboardData.leaderboard.length > 0 ? (
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white">
                                    <tr>
                                        <th className="px-6 py-4 text-left font-bold text-sm uppercase tracking-wider">
                                            <div className="flex items-center gap-2">
                                                <FaTrophy className="w-4 h-4" />
                                                Rank
                                            </div>
                                        </th>
                                        <th className="px-6 py-4 text-left font-bold text-sm uppercase tracking-wider">
                                            <div className="flex items-center gap-2">
                                                <MdPerson className="w-4 h-4" />
                                                User
                                            </div>
                                        </th>
                                        <th className="px-6 py-4 text-right font-bold text-sm uppercase tracking-wider">
                                            <div className="flex items-center justify-end gap-2">
                                                <FaCoins className="w-4 h-4" />
                                                Total Coins
                                            </div>
                                        </th>
                                        <th className="px-6 py-4 text-right font-bold text-sm uppercase tracking-wider">
                                            <div className="flex items-center justify-end gap-2">
                                                <FaQuestionCircle className="w-4 h-4" />
                                                Quizzes
                                            </div>
                                        </th>
                                        <th className="px-6 py-4 text-right font-bold text-sm uppercase tracking-wider">
                                            <div className="flex items-center justify-end gap-2">
                                                <MdTimer className="w-4 h-4" />
                                                Last Activity
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {leaderboardData.leaderboard.map((user) => {
                                        const rankDisplay = getRankDisplay(user.rank);
                                        return (
                                            <tr
                                                key={user.userId}
                                                className={`transition-all duration-300 hover:bg-gradient-to-r cursor-pointer hover:from-blue-50 hover:to-indigo-50 ${user.rank <= 3 ? rankDisplay.bgColor : ''
                                                    }`}
                                                onClick={() => handleEdit(user.userId)}
                                            >
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center border-2 ${rankDisplay.borderColor} ${rankDisplay.bgColor}`}>
                                                            {rankDisplay.icon}
                                                        </div>
                                                        <span className={`font-bold text-xl ${user.rank <= 3 ? rankDisplay.textColor : 'text-slate-700'}`}>
                                                            #{user.rank}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-4">
                                                        <div className="relative">
                                                            <img
                                                                src={user.profileImage}
                                                                alt={user.username}
                                                                onError={(e) => {
                                                                    e.target.onerror = null;
                                                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNFNUU3RUIiLz4KPHBhdGggZD0iTTIwIDE4QzIyLjIwOTEgMTggMjQgMTYuMjA5MSAyNCAxNEMyNCAxMS43OTA5IDIyLjIwOTEgMTAgMjAgMTBDMTcuNzkwOSAxMCAxNiAxMS43OTA5IDE2IDE0QzE2IDE2LjIwOTEgMTcuNzkwOSAxOCAyMCAxOFoiIGZpbGw9IiM5Q0EzQUYiLz4KPHBhdGggZD0iTTI4IDI4QzI4IDI0LjY4NjMgMjQuNDE4MyAyMiAyMCAyMkMxNS41ODE3IDIyIDEyIDI0LjY4NjMgMTIgMjhIMjhaIiBmaWxsPSIjOUNBM0FGIi8+Cjwvc3ZnPg==';
                                                                }}
                                                                className="w-12 h-12 rounded-xl border-2 border-white shadow-lg object-cover"
                                                            />
                                                            {user.rank <= 3 && (
                                                                <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center">
                                                                    <FaStar className="w-3 h-3 text-white" />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <p className="font-bold text-slate-800 text-lg">{user.username}</p>
                                                            <p className="text-slate-500 text-sm">User ID: {user.userId}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <div className="bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800 px-4 py-2 rounded-xl font-bold border-2 border-yellow-200">
                                                            {user.totalCoins.toLocaleString()}
                                                        </div>
                                                        <FaCoins className="w-5 h-5 text-yellow-600" />
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 px-4 py-2 rounded-xl font-bold border-2 border-blue-200 inline-block">
                                                        {user.quizCount}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 px-4 py-2 rounded-xl font-semibold border-2 border-gray-300 inline-block">
                                                        {formatDate(user.lastActivity)}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {renderPagination()}

                        {/* Total Results Info */}
                        <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-4 border-t border-gray-200">
                            <div className="flex items-center justify-center gap-3">
                                <IoPeople className="w-6 h-6 text-indigo-600" />
                                <span className="font-semibold text-slate-700">
                                    Showing {leaderboardData.leaderboard.length} of {leaderboardData.pagination.totalUsers.toLocaleString()} users
                                </span>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-20">
                        <div className="text-center">
                            <FaUserFriends className="w-20 h-20 mx-auto mb-6 text-slate-400" />
                            <h3 className="text-2xl font-bold text-slate-600 mb-2">No Leaderboard Data</h3>
                            <p className="text-slate-500">No users found for the selected time period.</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LeaderBoard;